import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';

export interface License {
  id: string;
  license_key: string;
  status: string;
  [k: string]: unknown;
}

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // omit ambiguous chars

function randomChunk(len: number): string {
  let out = '';
  const buf = new Uint8Array(len);
  crypto.getRandomValues(buf);
  for (let i = 0; i < len; i++) out += ALPHABET[buf[i] % ALPHABET.length];
  return out;
}

export function generateLicenseKey(): string {
  // NRX-{6 chars}-{8 chars} — readable, unambiguous
  return `NRX-${randomChunk(6)}-${randomChunk(8)}`;
}

export function generateDemoLicenseKey(): string {
  // NX-DEMO-XXXX-XXXX-XXXX — visually distinguishable in support tickets
  return `NX-DEMO-${randomChunk(4)}-${randomChunk(4)}-${randomChunk(4)}`;
}

export interface GetOrMintLicenseArgs {
  endClientId: string;
  /** When set, the end_client is expected to have is_demo=true and the
   *  license will be minted as employee_demo (non-billable, time-bound). */
  demoOpts?: {
    validUntilDays: number;
    assignedToEmployeeId: string | null;
  };
}

export function useGetOrMintLicense() {
  return useMutation({
    mutationFn: async (args: GetOrMintLicenseArgs): Promise<License> => {
      const { endClientId, demoOpts } = args;

      // Demo flow: always mint a fresh license (reps don't share demos across
      // sessions). Non-demo flow: reuse the existing active license if there
      // is one, matching today's behaviour.
      if (!demoOpts) {
        const { data: rows, error } = await supabase
          .from('neurax_licenses')
          .select('*')
          .eq('b2b_end_client_id', endClientId)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) throw new Error(`neurax_licenses lookup: ${error.message}`);
        if (rows && rows.length > 0) return rows[0] as unknown as License;
      }

      const userRes = await supabase.auth.getUser();
      const createdBy = userRes.data.user?.id ?? null;

      const insertRow: Record<string, unknown> = {
        b2b_end_client_id: endClientId,
        status: 'active',
        max_requests_per_day: 100,
        max_requests_per_month: 3000,
        features: { ai_chat: true, voice: true, screenshot: true },
      };
      if (createdBy) insertRow.created_by = createdBy;

      if (demoOpts) {
        const validUntil = new Date(Date.now() + demoOpts.validUntilDays * 86_400_000)
          .toISOString()
          .split('T')[0];
        insertRow.license_key = generateDemoLicenseKey();
        insertRow.license_type = 'employee_demo';
        insertRow.valid_until = validUntil;
        if (demoOpts.assignedToEmployeeId) {
          insertRow.assigned_to_employee_id = demoOpts.assignedToEmployeeId;
        }
      } else {
        insertRow.license_key = generateLicenseKey();
        insertRow.license_type = 'b2b_seat';
      }

      const { data: created, error: insertErr } = await supabase
        .from('neurax_licenses')
        .insert(insertRow as never)
        .select()
        .single();

      if (insertErr || !created) {
        throw new Error(`neurax_licenses insert: ${insertErr?.message ?? 'failed'}`);
      }
      return created as unknown as License;
    },
  });
}
